<template>
  <div
    class="sprite-editor w-full h-full bg-base-200 overflow-hidden grid grid-rows-[auto_1fr_auto]"
  >
    <ThreadsNav show-back show-center :go-back="() => emit('onClose')">
      <template #center>
        <button class="btn btn-xs btn-outline" @click="handleResetView">
          重置视图
        </button>
      </template>

      <template #right>
        <button class="btn btn-xs btn-outline" @click="handlePlay">播放</button>

        <button class="btn btn-xs btn-primary" @click="handleExport">
          {{ $t(`Buttons.save`) }}
        </button>
      </template>
    </ThreadsNav>

    <!-- 主体区域 -->
    <div class="flex relative overflow-hidden">
      <!-- 帧选择器 -->
      <div
        class="frame-selector-container transition-all duration-300 ease-in-out"
        :class="{
          'w-20 sm:w-24': !isFrameSelectorCollapsed,
          'w-0 overflow-hidden': isFrameSelectorCollapsed,
        }"
      >
        <FrameSelector
          :frames="frameState.frames.value"
          :current-frame-index="frameState.currentFrameIndex.value"
          @frame-select="frameState.selectFrame"
        />
      </div>

      <!-- 收起/展开按钮 -->
      <button
        :class="[
          'absolute top-2 z-10 btn btn-xs btn-ghost bg-primary text-primary-content shadow-md transition-all duration-300',
          isFrameSelectorCollapsed ? 'left-0' : 'left-[72px] sm:left-[88px]',
        ]"
        @click="toggleFrameSelector"
      >
        <span class="text-xs">{{
          isFrameSelectorCollapsed ? ">>" : "<<"
        }}</span>
      </button>

      <!-- 画布 -->
      <div class="flex-1 min-w-0">
        <Canvas
          ref="canvasRef"
          :current-frame="frameState.currentFrame.value"
          :mode="canvasState.mode.value"
          :color="canvasState.color.value"
          :fill="canvasState.fill.value"
          @history-add="frameState.saveToHistory"
          @frame-modified="frameState.markFrameAsModified"
          @frame-updated="frameState.updateFrameData"
          @mode-change="canvasState.setMode"
          @color-picked="canvasState.setColor"
        />
      </div>
    </div>

    <!-- 工具栏 -->
    <Toolbar
      :canvas-state="canvasState.getState()"
      :history-state="frameState.getHistoryState()"
      @mode-change="canvasState.setMode"
      @color-change="canvasState.setColor"
      @undo="handleUndo"
      @redo="handleRedo"
    />

    <!-- 动画播放器 -->
    <AnimationPlayer
      :show="showAnimationPlayer"
      :frames="frameState.frames.value"
      @close="showAnimationPlayer = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, nextTick } from "vue";
import Toolbar from "./SpriteToolbar.vue";
import FrameSelector from "./FrameSelector.vue";
import Canvas from "./EditorCanvas.vue";
import AnimationPlayer from "./AnimationPlayer.vue";
import { useCanvasState } from "./composables/useCanvasState";
import { useFrameState } from "./composables/useFrameState";
import { CANVAS_CONFIG } from "./config";

const props = defineProps<{
  imageUrls: string[];
  frameIndex?: number;
}>();
const emit = defineEmits<{
  (e: "onConfirm", files: File[]): void;
  (e: "onClose"): void;
}>();

// 状态管理
const canvasState = useCanvasState();
const frameState = useFrameState();

// Canvas 组件引用
const canvasRef = ref<InstanceType<typeof Canvas>>();

// 动画播放器状态
const showAnimationPlayer = ref(false);

// FrameSelector 收起状态
const isFrameSelectorCollapsed = ref(false);

// 事件处理
const handleUndo = () => {
  const imageData = frameState.undo();
  if (imageData && canvasRef.value) {
    canvasRef.value.restoreFromHistory(imageData);
  }
};

const handleRedo = () => {
  const imageData = frameState.redo();
  if (imageData && canvasRef.value) {
    canvasRef.value.restoreFromHistory(imageData);
  }
};

const handleResetView = () => {
  if (canvasRef.value) {
    canvasRef.value.resetView();
  }
};

const handlePlay = () => {
  // 直接显示动画播放器
  showAnimationPlayer.value = true;
};

const toggleFrameSelector = () => {
  isFrameSelectorCollapsed.value = !isFrameSelectorCollapsed.value;

  // 延迟触发Canvas尺寸重新计算，等待CSS动画完成
  nextTick(() => {
    setTimeout(() => {
      if (canvasRef.value) {
        // 直接调用Canvas的handleResize方法
        canvasRef.value.handleResize();
      }
    }, 300); // 等待CSS过渡动画完成
  });
};

const handleExport = async () => {
  const files: File[] = [];

  for (let i = 0; i < frameState.frames.value.length; i++) {
    const frame = frameState.frames.value[i];

    // 如果是当前帧且有修改，先保存当前编辑状态
    if (i === frameState.currentFrameIndex.value && canvasRef.value) {
      const currentImageData = await getCurrentFrameImageData();
      if (currentImageData) {
        frameState.updateFrameData(currentImageData);
      }
    }

    const canvas = document.createElement("canvas");
    canvas.width = CANVAS_CONFIG.SPRITE_WIDTH;
    canvas.height = CANVAS_CONFIG.SPRITE_HEIGHT;
    const ctx = canvas.getContext("2d")!;

    const img = new Image();
    img.crossOrigin = "anonymous";
    await new Promise((resolve) => {
      img.onload = resolve;
      img.src = frame.url;
    });

    ctx.drawImage(
      img,
      0,
      0,
      CANVAS_CONFIG.SPRITE_WIDTH,
      CANVAS_CONFIG.SPRITE_HEIGHT
    );

    const blob = await new Promise<Blob>((resolve) => {
      canvas.toBlob((blob) => resolve(blob!), "image/png");
    });

    const file = new File([blob], `frame_${i + 1}.png`, { type: "image/png" });
    files.push(file);
  }

  emit("onConfirm", files);
};

const getCurrentFrameImageData = async (): Promise<ImageData | null> => {
  if (!canvasRef.value) return null;

  // 调用Canvas组件的方法获取当前sprite数据
  return canvasRef.value.getCurrentSpriteImageData?.() || null;
};

// 初始化
onMounted(async () => {
  await frameState.initializeFrames(props.imageUrls);
  if (props.frameIndex) {
    frameState.selectFrame(props.frameIndex);
    nextTick(() => {
      canvasRef.value?.resetView();
    });
  }
});

// 监听图片URL变化
watch(
  () => props.imageUrls,
  async () => {
    await frameState.initializeFrames(props.imageUrls);
  }
);

// 监听FrameSelector收起状态变化，触发Canvas尺寸重新计算
watch(isFrameSelectorCollapsed, () => {
  nextTick(() => {
    setTimeout(() => {
      if (canvasRef.value) {
        // 直接调用Canvas的handleResize方法
        canvasRef.value.handleResize();
      }
    }, 320); // 稍微超过CSS过渡时间
  });
});
</script>

<style scoped>
.sprite-editor {
  user-select: none;
}
</style>
